// Components==============
import storeWrongAnswer from 'actions/wrongAnswer/store';
import React, { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { HomeworkContext } from 'screens/Homework/Homework';
import {
    highlightChars,
    HighlightExerciseContext,
} from '../../HighlightExercise';
import { highlightString } from '../../utils/highlightString';
import { stripHighlightChars } from '../../utils/stripHighlightChars';
import './Sentence.module.scss';
// =========================

const Word = ({
    word,
    id,
    wordIndex,
}: {
    word: string;
    id: string;
    wordIndex: number;
}) => {
    const { answeredSentences } = useContext(HighlightExerciseContext);

    const isAnswered = (
        answeredSentences.find((as) => as.id === id)?.words || []
    ).includes(wordIndex);

    const highlightedWord = highlightString(word);
    const readableWord = stripHighlightChars(word);

    return (
        <span styleName="word">
            {isAnswered ? (
                <span dangerouslySetInnerHTML={{ __html: highlightedWord }} />
            ) : (
                readableWord
            )}
            <wbr />
        </span>
    );
};

export default function Sentence({
    index,
    status,
    wrong,
    setWrong,
}: {
    index: number;
    status: 'initial' | 'answered' | 'correct';
    wrong: boolean;
    setWrong: React.Dispatch<React.SetStateAction<boolean>>;
}) {
    const { sequence } = useContext(HomeworkContext);

    const {
        setSelected,
        answeredSentences,
        sentences,
        readSentenceSequence,
        setTextToBeRead,
    } = useContext(HighlightExerciseContext);

    const dispatch = useDispatch();

    const readableSentence = stripHighlightChars(sentences[index].sentence);

    const sentenceShouldBeHighlighted = !!highlightChars.find((char) =>
        sentences[index].sentence.includes(char)
    );

    const { homeworkId, treatmentId } = useParams() as {
        homeworkId: string;
        treatmentId: string;
    };

    const alreadyAnswered = answeredSentences.find(
        (as) => as.id === sentences[index].id
    );

    function handleSentenceClick() {
        // prevent double click
        if (wrong) return;

        const isAnsweredCorrect = alreadyAnswered?.status === 'correct';

        // sentence should be highlighted and isn't answered completely yet
        if (sentenceShouldBeHighlighted && !isAnsweredCorrect)
            return setSelected((prev) => ({
                ...prev,
                id: sentences[index].id,
                index,
                type: 'sentence',
            }));

        // Sentence shouldn't be highlighted
        dispatch(
            storeWrongAnswer({
                given_answer: 'verkeerd geklikt',
                correct_answer: sentences[index].sentence,
                type: 'highlight',
                metadata: { sequence },
                exercise_id: homeworkId,
                exercise_type: 'homework',
                treatment_id: treatmentId,
            })
            // @ts-ignore
        ).then(() => {
            setWrong(true);
        });
    }

    const isReading = readSentenceSequence === index;

    useEffect(() => {
        if (isReading) setTextToBeRead(readableSentence);
    }, [readSentenceSequence]);

    const sentenceSplitUpInWords = sentences?.[index]?.sentence.split(' ');

    return (
        <div
            styleName={`sentence ${isReading ? 'reading' : ''}`}
            onClick={handleSentenceClick}
        >
            {status === 'initial' ? (
                <p>{readableSentence}</p>
            ) : (
                <p>
                    {sentenceSplitUpInWords.map((word, i) => (
                        <Word
                            word={word}
                            id={sentences[index].id}
                            wordIndex={i}
                            key={i}
                        />
                    ))}
                </p>
            )}
        </div>
    );
}
